import { For, Show, createEffect, createSignal } from "solid-js";
import { getTemplate, listTemplates, saveTemplate } from "../api/reports";
import toast from "solid-toast";

export default function TemplatesModal(props: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [items, setItems] = createSignal<
    { key: string; title: string; version: number }[]
  >([]);
  const [active, setActive] = createSignal<string>("");
  const [json, setJson] = createSignal<string>("{}");
  const [edit, setEdit] = createSignal(false);
  const [loading, setLoading] = createSignal(false);

  async function load() {
    setLoading(true);
    try {
      const res = await listTemplates();
      setItems(res.templates);
      const first = res.templates[0]?.key;
      if (first) select(first);
    } catch (e: any) {
      toast.error(e.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }
  async function select(key: string) {
    try {
      const t = await getTemplate(key);
      setActive(key);
      setJson(JSON.stringify(t, null, 2));
      setEdit(false);
    } catch (e: any) {
      toast.error(e.message || "Failed to load template");
    }
  }
  async function save() {
    try {
      const data = JSON.parse(json());
      const key = active();
      await saveTemplate(key, data);
      toast.success("Template saved (new version)");
      await load();
    } catch (e: any) {
      toast.error(e.message || "Invalid JSON or save failed");
    }
  }

  createEffect(() => {
    if (props.isOpen) load();
  });

  function parsed() {
    try {
      return JSON.parse(json());
    } catch {
      return null;
    }
  }

  return (
    <Show when={props.isOpen}>
      <div class="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center p-4">
        <div class="bg-white rounded-md shadow-xl w-[min(1100px,95vw)] max-h-[90vh] flex">
          <div class="w-56 border-r p-2 overflow-auto">
            <For each={items()}>
              {(t) => (
                <button
                  class="w-full text-left px-2 py-1 rounded hover:bg-gray-100"
                  onClick={() => select(t.key)}
                >
                  <div class="text-sm font-medium">{t.title}</div>
                  <div class="text-[11px] text-gray-500">
                    {t.key} • v{t.version}
                  </div>
                </button>
              )}
            </For>
          </div>
          <div class="flex-1 flex flex-col">
            <div class="flex items-center justify-between px-3 py-2 border-b">
              <div class="font-semibold">Template: {active() || ""}</div>
              <div class="flex items-center gap-2">
                <button
                  class="px-2 py-1 text-sm border rounded"
                  onClick={() => setEdit((e) => !e)}
                >
                  {edit() ? "Preview" : "Edit JSON (POC)"}
                </button>
                <button
                  class="px-2 py-1 text-sm border rounded"
                  onClick={props.onClose}
                >
                  ✖ Close
                </button>
              </div>
            </div>
            <div class="flex-1 overflow-auto p-3">
              <Show when={!edit()}>
                <div class="space-y-3">
                  <div>
                    <div class="text-xs text-gray-500">Title</div>
                    <div class="text-sm font-semibold">{parsed()?.title}</div>
                  </div>
                  <div>
                    <div class="text-xs text-gray-500">Report Type</div>
                    <div class="text-sm">{parsed()?.reportType}</div>
                  </div>
                  <div>
                    <div class="text-xs text-gray-500">Sections</div>
                    <ul class="list-disc list-inside text-sm space-y-1">
                      <For each={(parsed()?.sections || []) as any[]}>
                        {(s: any) => <li>{s.title}</li>}
                      </For>
                    </ul>
                  </div>
                  <div>
                    <div class="text-xs text-gray-500">Required Fields</div>
                    <div class="flex flex-wrap gap-2">
                      <For each={(parsed()?.requiredFields || []) as any[]}>
                        {(f: string) => (
                          <span class="text-[11px] px-2 py-[2px] border rounded bg-gray-50">
                            {f}
                          </span>
                        )}
                      </For>
                    </div>
                  </div>
                </div>
              </Show>
              <Show when={edit()}>
                <textarea
                  class="w-full h-[60vh] font-mono text-sm border rounded p-2"
                  value={json()}
                  onInput={(e) => setJson(e.currentTarget.value)}
                />
                <div class="mt-2">
                  <button
                    class="px-3 py-1 text-sm border rounded"
                    onClick={save}
                  >
                    Save
                  </button>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </Show>
  );
}
